/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Call recording and transcription settings.
 *
 * The pipeline behind this page was built before the page was: recordings are
 * already mirrored out of Twilio into the tenant's own R2 bucket, transcribed
 * with Groq whisper-large-v3, and purged on a retention timer. None of it ran,
 * because nothing in the product could turn it on -- `transcription_enabled`
 * defaults to false and no screen exposed it.
 *
 * The consent controls are not decoration. Both the UAE and several Australian
 * states require the other party to be told, and "we announced it" is only
 * defensible if you can show what was announced on that specific call, which
 * is why an edit to the announcement bumps a version the call record keeps.
 */

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Card,
  CircularProgress,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  Switch,
  TextField,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "auth/Auth";
import colors from "styles/tokens/colors";
import { getApiErrorMessage } from "utils/apiErrors";
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";

const CARD_BORDER = `1px solid ${colors.callCenter.cardBorder}`;

const CONSENT_MODES = [
  {
    value: "none",
    label: "No announcement",
    hint: "Record without telling the caller. Only lawful in some jurisdictions — check before choosing this.",
  },
  {
    value: "announce",
    label: "Announce the recording",
    hint: "The caller hears a notice before the call connects. The usual choice.",
  },
  {
    value: "announce_and_require_optin",
    label: "Announce and require opt-in",
    hint: "The caller is told and must actively agree before recording starts.",
  },
];

// Whisper handles far more than this; these are the languages this deployment
// actually sees. `auto` lets the model detect it, at some cost to accuracy on
// short calls.
const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "ar", label: "Arabic" },
  { value: "hi", label: "Hindi" },
  { value: "ur", label: "Urdu" },
  { value: "fr", label: "French" },
  { value: "auto", label: "Detect automatically" },
];

/** A labelled switch with the explanation underneath, not in a tooltip. */
function ToggleRow({ label, hint, checked, onChange, disabled }) {
  return (
    <MDBox display="flex" alignItems="flex-start" justifyContent="space-between" gap={2} py={1.5}>
      <MDBox flexGrow={1} pr={2}>
        <MDTypography variant="button" fontWeight="medium" color="dark" display="block">
          {label}
        </MDTypography>
        {hint ? (
          <MDTypography variant="caption" color="text">
            {hint}
          </MDTypography>
        ) : null}
      </MDBox>
      <Switch checked={Boolean(checked)} onChange={onChange} disabled={disabled} />
    </MDBox>
  );
}

function SectionCard({ title, description, children }) {
  return (
    <Card sx={{ p: 3, border: CARD_BORDER, boxShadow: "none", borderRadius: "12px", height: "100%" }}>
      <MDTypography variant="h5" fontWeight="medium" mb={0.5}>
        {title}
      </MDTypography>
      {description ? (
        <MDTypography variant="body2" color="text" mb={2}>
          {description}
        </MDTypography>
      ) : null}
      <Divider sx={{ mb: 1 }} />
      {children}
    </Card>
  );
}

export default function TelephonyRecordingPage() {
  const isV2 = useTheme()?.pearlUi === "v2";
  const navigate = useNavigate();
  const { axiosInstance } = useAuth();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [settings, setSettings] = useState(null);
  const [form, setForm] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get("/telephony/settings");
      setSettings(res.data);
      setForm({
        recording_enabled: Boolean(res.data.recording_enabled),
        recording_consent_mode: res.data.recording_consent_mode || "announce",
        recording_announcement_text: res.data.recording_announcement_text || "",
        recording_announcement_url: res.data.recording_announcement_url || "",
        delete_from_twilio_after_mirror: Boolean(res.data.delete_from_twilio_after_mirror),
        recording_retention_days:
          res.data.recording_retention_days === null ||
          res.data.recording_retention_days === undefined
            ? ""
            : String(res.data.recording_retention_days),
        transcription_enabled: Boolean(res.data.transcription_enabled),
        transcription_language: res.data.transcription_language || "en",
      });
    } catch (err) {
      setError(getApiErrorMessage(err, "Unable to load recording settings."));
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => {
    load();
  }, [load]);

  const set = (key) => (value) => setForm((f) => ({ ...f, [key]: value }));

  const consentHint = useMemo(
    () => CONSENT_MODES.find((m) => m.value === form?.recording_consent_mode)?.hint || "",
    [form]
  );

  // Retention is optional, but a value that is present has to be a real number
  // of days or the nightly purge silently does nothing.
  const retentionError = useMemo(() => {
    const raw = (form?.recording_retention_days ?? "").trim?.() ?? "";
    if (!raw) return "";
    const n = Number(raw);
    if (!Number.isInteger(n) || n < 1 || n > 3650) {
      return "Enter a whole number of days between 1 and 3650, or leave blank to keep recordings indefinitely.";
    }
    return "";
  }, [form]);

  const save = async () => {
    if (retentionError) return;
    setSaving(true);
    setError("");
    try {
      const retention = (form.recording_retention_days ?? "").trim?.() ?? "";
      const res = await axiosInstance.put("/telephony/settings", {
        recording_enabled: form.recording_enabled,
        recording_consent_mode: form.recording_consent_mode,
        recording_announcement_text: form.recording_announcement_text.trim() || null,
        recording_announcement_url: form.recording_announcement_url.trim() || null,
        delete_from_twilio_after_mirror: form.delete_from_twilio_after_mirror,
        recording_retention_days: retention ? Number(retention) : null,
        transcription_enabled: form.transcription_enabled,
        // The provider is fixed for now; the column exists so a second one can
        // be added without a migration.
        transcription_provider: form.transcription_enabled ? "groq" : null,
        transcription_language: form.transcription_language,
      });
      setSettings(res.data);
      setToast("Recording settings saved.");
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not save recording settings."));
    } finally {
      setSaving(false);
    }
  };

  const noTranscriptionKey =
    form?.transcription_enabled && settings && settings.transcription_key_available === false;

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={isV2 ? 0 : 4} pb={6} px={isV2 ? 0 : 2}>
        {isV2 ? (
          <PageHeader
            title="Recording & transcription"
            summary="Choose what is recorded, what callers are told, how long audio is kept, and whether calls are transcribed."
          />
        ) : (
          <MDBox display="flex" alignItems="center" gap={1} mb={3}>
            <IconButton onClick={() => navigate("/settings/telephony")}>
              <ArrowBack />
            </IconButton>
            <MDBox>
              <MDTypography variant="h4" fontWeight="medium">
                Recording &amp; transcription
              </MDTypography>
              <MDTypography variant="body2" color="text">
                Choose what is recorded, what callers are told, how long audio is
                kept, and whether calls are transcribed.
              </MDTypography>
            </MDBox>
          </MDBox>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {/* The one failure the backend cannot report in the UI: transcription
            bills the tenant's own key, and without one every call lands in
            transcription_status 'failed' with nothing on screen to explain it. */}
        {noTranscriptionKey && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Transcription uses your organisation&apos;s own AI API key, and none is
            set. Add one under Settings → Integrations, or calls will record but
            never transcribe.
          </Alert>
        )}

        {loading || !form ? (
          <MDBox display="flex" justifyContent="center" py={8}>
            <CircularProgress />
          </MDBox>
        ) : (
          <>
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <SectionCard
                  title="Recording"
                  description="Audio is captured on the conference, so transfers and supervisor joins are included in a single file."
                >
                  <ToggleRow
                    label="Record calls"
                    hint="Applies to inbound and outbound calls alike."
                    checked={form.recording_enabled}
                    onChange={(e) => set("recording_enabled")(e.target.checked)}
                  />

                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Caller consent"
                    value={form.recording_consent_mode}
                    disabled={!form.recording_enabled}
                    onChange={(e) => set("recording_consent_mode")(e.target.value)}
                    helperText={consentHint}
                    sx={{ mt: 2 }}
                  >
                    {CONSENT_MODES.map((m) => (
                      <MenuItem key={m.value} value={m.value}>
                        {m.label}
                      </MenuItem>
                    ))}
                  </TextField>

                  <TextField
                    fullWidth
                    multiline
                    rows={2}
                    size="small"
                    label="Announcement text"
                    placeholder="This call may be recorded for quality and training purposes."
                    value={form.recording_announcement_text}
                    disabled={!form.recording_enabled || form.recording_consent_mode === "none"}
                    onChange={(e) => set("recording_announcement_text")(e.target.value)}
                    helperText="Spoken to the caller before the call connects. Editing this records a new announcement version against future calls."
                    sx={{ mt: 2 }}
                  />

                  <TextField
                    fullWidth
                    size="small"
                    label="Announcement audio URL (optional)"
                    placeholder="https://…/recording-notice.mp3"
                    value={form.recording_announcement_url}
                    disabled={!form.recording_enabled || form.recording_consent_mode === "none"}
                    onChange={(e) => set("recording_announcement_url")(e.target.value)}
                    helperText="A recorded notice to play instead of the text above."
                    sx={{ mt: 2 }}
                  />
                </SectionCard>
              </Grid>

              <Grid item xs={12} md={6}>
                <SectionCard
                  title="Storage &amp; retention"
                  description="Recordings are copied into your own storage as soon as Twilio finishes them."
                >
                  <ToggleRow
                    label="Delete from Twilio after copying"
                    hint="Recommended. Once the copy succeeds, the audio lives in one place you control rather than two."
                    checked={form.delete_from_twilio_after_mirror}
                    onChange={(e) => set("delete_from_twilio_after_mirror")(e.target.checked)}
                    disabled={!form.recording_enabled}
                  />

                  <TextField
                    fullWidth
                    size="small"
                    label="Delete recordings after (days)"
                    placeholder="Leave blank to keep indefinitely"
                    value={form.recording_retention_days}
                    disabled={!form.recording_enabled}
                    onChange={(e) => set("recording_retention_days")(e.target.value)}
                    error={Boolean(retentionError)}
                    helperText={
                      retentionError ||
                      "Audio is deleted on a nightly sweep. Transcripts are kept — they are a business record, not voice data."
                    }
                    sx={{ mt: 2 }}
                  />
                </SectionCard>
              </Grid>

              <Grid item xs={12}>
                <SectionCard
                  title="Transcription"
                  description="Transcripts are searchable alongside the call and can be clicked to jump to that moment in the audio."
                >
                  <ToggleRow
                    label="Transcribe recorded calls"
                    hint="Runs after the recording is stored. Requires recording to be on, and uses your organisation's AI API key."
                    checked={form.transcription_enabled}
                    onChange={(e) => set("transcription_enabled")(e.target.checked)}
                    disabled={!form.recording_enabled}
                  />

                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid item xs={12} md={4}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        label="Spoken language"
                        value={form.transcription_language}
                        disabled={!form.transcription_enabled}
                        onChange={(e) => set("transcription_language")(e.target.value)}
                        helperText="Naming the language beats auto-detection on short calls."
                      >
                        {LANGUAGES.map((l) => (
                          <MenuItem key={l.value} value={l.value}>
                            {l.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Grid>
                  </Grid>

                  {!form.recording_enabled && (
                    <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 1 }}>
                      There is nothing to transcribe while recording is off.
                    </MDTypography>
                  )}

                  <MDTypography variant="caption" color="text" sx={{ display: "block", mt: 2 }}>
                    Conference audio is mixed to one channel, so transcripts are
                    not split by speaker.
                  </MDTypography>
                </SectionCard>
              </Grid>
            </Grid>

            <MDBox display="flex" justifyContent="flex-end" gap={1.5} mt={3}>
              <MDButton variant="outlined" color="info" onClick={load} disabled={saving}>
                Discard changes
              </MDButton>
              <MDButton
                variant="gradient"
                color="info"
                onClick={save}
                disabled={saving || Boolean(retentionError)}
              >
                {saving ? <CircularProgress size={18} color="inherit" /> : "Save settings"}
              </MDButton>
            </MDBox>
          </>
        )}
      </MDBox>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={4000}
        onClose={() => setToast("")}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert severity="success" onClose={() => setToast("")} sx={{ width: "100%" }}>
          {toast}
        </Alert>
      </Snackbar>
    </DashboardLayout>
  );
}
